import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { exportApi } from '../../api/exports'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useExportWizard } from '../../export/ExportWizardContext'
import { useAsync } from '../../hooks/useAsync'
import { formatInt } from '../../lib/format'
import { EXPORT_STEPS } from './steps'

/* Step 3 — Preview. Shows the first page of records exactly as they will
   appear in the downloaded Excel: the same column headers and the same
   one-line-per-item-line layout. The columns come from the server so the
   preview and the download are guaranteed to match. */

const PAGE_SIZE = 25

/** Max page buttons shown at once before collapsing into an ellipsis. */
const PAGER_WINDOW = 7

function totalPages(totalCount: number | null, pageSize: number): number {
  if (totalCount == null) return 0
  return Math.max(1, Math.ceil(totalCount / pageSize))
}

/** Compact page list like [1, '…', 4, 5, 6, '…', 12] around the current page. */
function pagerPages(current: number, last: number): Array<number | '…'> {
  if (last <= PAGER_WINDOW) return Array.from({ length: last }, (_, i) => i + 1)
  const pages: Array<number | '…'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(last - 1, current + 1)
  if (start > 2) pages.push('…')
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < last - 1) pages.push('…')
  pages.push(last)
  return pages
}

export function ExportFieldsPage() {
  const wizard = useExportWizard()
  const { current } = useCompanies()
  const [page, setPage] = useState(1)

  const preview = useAsync(
    () => {
      const companyId = current?.id
      if (!companyId || !wizard.entityType) return Promise.resolve(null)
      return exportApi.preview({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        reference: wizard.reference || undefined,
        dateMode: wizard.dateMode,
        preset: wizard.dateMode === 'preset' ? wizard.preset : undefined,
        from: wizard.dateMode === 'custom' ? wizard.from || undefined : undefined,
        to: wizard.dateMode === 'custom' ? wizard.to || undefined : undefined,
        page,
        pageSize: PAGE_SIZE,
      })
    },
    [
      current?.id,
      wizard.entityType,
      wizard.reference,
      wizard.dateMode,
      wizard.preset,
      wizard.from,
      wizard.to,
      page,
    ],
  )

  if (!wizard.entityType) return <Navigate to="/app/export" replace />

  const subject = wizard.entity?.label ?? wizard.entityType
  const count = preview.data?.totalCount ?? null
  const last = totalPages(count, PAGE_SIZE)

  // Use the server-resolved template columns and expanded rows so that the
  // preview shows exactly what the downloaded Excel will contain.
  const columns = preview.data?.exportColumns ?? []
  const rows = preview.data?.exportedRows ?? []

  return (
    <div className="bb bb-wizard bb-wizard-wide bb-export-preview">
      {/* A table-heavy page needs vertical room more than it needs the default
          generous breathing space. Everything below is scoped to this page so
          the other wizard steps keep their own spacing.

          The chrome this trims: the wizard's own 32px top/bottom padding (the
          shell's <main> already pads 34/72), the stepper's 48px bottom margin
          (tuned for the shorter wizard screens), and <main>'s 72px bottom
          padding, which is what actually pushed the app footer past the fold. */}
      <style>{`
        .bb-export-preview { padding-top: 0; padding-bottom: 8px }
        .bb-export-preview .bb-steps { margin-bottom: 16px }
        .bb-export-preview .bb-activity-head { margin-bottom: 8px }
      `}</style>

      <BbStepper steps={EXPORT_STEPS} current={2} />

      <div className="bb-activity-head">
        <div>
          <h2>Preview {subject}</h2>
          <p className="bb-map-help">
            Showing columns as they will appear in the downloaded Excel
          </p>
        </div>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-quiet" to="/app/export/filters">
            Previous
          </Link>
          <Link className="bb-btn bb-btn-green" to="/app/export/done">
            Next
          </Link>
        </span>
      </div>

      {preview.error ? (
        <Alert tone="warn" title="Could not preview from QuickBooks">
          {preview.error.message}
        </Alert>
      ) : columns.length === 0 && !preview.loading ? (
        <div className="bb-empty-ref">
          <BbIcon name="sheet" />
          <h3>No records to preview</h3>
          <p>No records match the current filters for {subject}.</p>
        </div>
      ) : (
        <div className="bb-activity" style={{ marginTop: 12 }}>
          {/* Bounded so the table body scrolls in place instead of pushing the
              pager and the app footer off screen. The 550px is the measured
              chrome around this box at 1600x900: the 64px top bar, <main>'s
              34/72 padding, the stepper (76+16), the heading row (72+8), the
              pager (53) and the 53px app footer. The wizard's own 32/32 is
              trimmed to 0/8 by the page-scoped rule above, so it is not in the
              sum. The 240px floor keeps the table readable on a short window,
              where the page scrolls rather than the table collapsing. */}
          <div
            className="bb-export-preview-scroll"
            style={{
              overflow: 'auto',
              maxHeight: 'max(240px, calc(100dvh - 550px))',
            }}
          >
          <table className="bb-rtable" style={{ minWidth: 'max-content' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {columns.map((col, i) => (
                  <th key={i}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.loading ? (
                <tr>
                  <td colSpan={columns.length || 1} className="bb-map-help">
                    Loading records from QuickBooks…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length || 1} className="bb-map-help">
                    No records match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => (
                      <td key={colIndex}>{cell || '—'}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          <div className="bb-pager">
            <span className="bb-pager-n">
              {count == null
                ? 'Count unavailable'
                : `${formatInt(count)} record${count === 1 ? '' : 's'} in QuickBooks`}
            </span>
            <span className="bb-pager-c">
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === 1}
                aria-label="First page"
                onClick={() => setPage(1)}
              >
                |&lsaquo;
              </button>
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === 1}
                aria-label="Previous page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>
              {pagerPages(page, last).map((n, index) =>
                n === '…' ? (
                  <span className="bb-pager-b bb-pager-ellipsis" key={`e-${index}`}>
                    …
                  </span>
                ) : (
                  <button
                    className={n === page ? 'bb-pager-b bb-pager-on' : 'bb-pager-b'}
                    type="button"
                    key={n}
                    aria-current={n === page ? 'page' : undefined}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === last}
                aria-label="Next page"
                onClick={() => setPage((p) => Math.min(last, p + 1))}
              >
                &rsaquo;
              </button>
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === last}
                aria-label="Last page"
                onClick={() => setPage(last)}
              >
                &rsaquo;|
              </button>
            </span>
          </div>
        </div>
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/export/filters">
          <BbIcon name="arrow-left" />
          Previous
        </Link>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-green" to="/app/export/done">
            Next
            <BbIcon name="arrow-right" />
          </Link>
        </span>
      </div>
    </div>
  )
}
